import React, { useMemo, useState } from 'react';
import { Plane, Search, BookOpen, Info } from 'lucide-react';
import { AMM_MODELS, AMM_TASKS, COM_18702_TASKS, COM_18702_MODELS } from '../data/ammTasks';

export const TOOL_PRESETS = {
  'SPL-1473': {
    models: AMM_MODELS,
    tasks: AMM_TASKS,
    defaultModel: '737-678',
    badge: 'Boeing AMM / FIM Task Finder',
    heading: 'Which Boeing tasks call out the UL101?',
    intro: <>Pick your airplane model to see every Boeing AMM and FIM task that lists tool tag <span className="font-mono font-semibold text-[#1e2d3b]">SPL-1473</span> — the CTRL UL101 (P/N B00033).</>,
    footnote: "Source: Boeing tool details for B00033 (reference only). Task numbers vary by minor model and revision — always follow your operator's current AMM / FIM.",
    notice: null,
  },
  'COM-18702': {
    models: COM_18702_MODELS,
    tasks: COM_18702_TASKS,
    defaultModel: '737-678',
    badge: 'Boeing tool tag COM-18702 — FOTRIC H-Series offered as approved equivalent',
    heading: 'Which Boeing tasks call out an acoustic camera?',
    intro: <>Pick your airplane model to see the Boeing AMM tasks that call out tool tag <span className="font-mono font-semibold text-[#1e2d3b]">COM-18702</span> (Digital Camera – Acoustic). FOTRIC H-Series imagers are offered as approved equivalents under airline tool-equivalency programs.</>,
    footnote: "Adoption requires the operator's own Part 145 tool-equivalency documentation and quality-organization sign-off. Task numbers vary by minor model and revision — always follow your operator's current AMM / FIM.",
    notice: 'Additional model coverage pending Boeing tool-details review. Only tasks confirmed from the Boeing AMM are listed here.',
  },
};

const CATEGORY_ORDER = [
  'Ultrasonic leak detection',
  'Intermittent wiring inspection',
  'Door, hatch & window leak checks',
  'Parking brake fault isolation',
  'Oxygen system fault isolation',
  'ECS health checks & inspections',
  'ECS component installation leak checks',
  'Nitrogen generation system',
  'Other',
];

const TaskGroup = ({ category, tasks }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6" data-testid="amm-task-group">
    <h4 className="text-sm font-bold uppercase tracking-wide text-blue-700 mb-3">{category}</h4>
    <ul className="divide-y divide-slate-100">
      {tasks.map((t) => (
        <li key={`${t.doc}-${t.number}`} className="py-2.5 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4" data-testid="amm-task-row">
          <span className="shrink-0 font-mono text-xs sm:text-sm font-semibold text-[#1e2d3b]">
            <span className={`inline-block mr-2 rounded px-1.5 py-0.5 text-[10px] font-bold ${t.doc === 'FIM' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>{t.doc}</span>
            {t.number}
          </span>
          <span className="text-sm text-slate-700">{t.title}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const AmmTaskFinder = ({ tool = 'SPL-1473' }) => {
  const preset = TOOL_PRESETS[tool];
  const [model, setModel] = useState(preset.defaultModel);
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = preset.tasks.filter((t) => t.model === model && (!q || `${t.number} ${t.title}`.toLowerCase().includes(q)));
    return CATEGORY_ORDER.map((c) => ({ category: c, tasks: rows.filter((t) => t.category === c) })).filter((g) => g.tasks.length);
  }, [model, query, preset.tasks]);

  const selected = preset.models.find((m) => m.key === model);
  const count = groups.reduce((n, g) => n + g.tasks.length, 0);

  return (
    <section className="py-14 sm:py-20 bg-white border-t border-slate-200" data-testid="amm-task-finder" data-tool={tool}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#1e2d3b] text-white px-4 py-1.5 text-sm font-semibold mb-3 max-w-full text-left">
              <BookOpen className="h-4 w-4 text-orange-400 shrink-0" /> <span data-testid="amm-task-badge">{preset.badge}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e2d3b] leading-tight" data-testid="amm-task-heading">{preset.heading}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{preset.intro}</p>
            {preset.notice && (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900" data-testid="amm-task-notice">
                <Info className="h-4 w-4 mt-0.5 shrink-0 text-orange-500" />
                <p>{preset.notice}</p>
              </div>
            )}
            <div className="mt-6 space-y-3">
              <label htmlFor="amm-model" className="block text-xs font-bold uppercase tracking-wide text-slate-500">Airplane model</label>
              <div className="relative">
                <Plane className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-600" />
                <select
                  id="amm-model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="w-full appearance-none rounded-xl border border-slate-300 bg-white pl-10 pr-10 py-3 text-sm font-semibold text-[#1e2d3b] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="amm-model-select"
                >
                  {preset.models.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
                <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">▾</span>
              </div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter by task number or title…"
                  className="w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="amm-task-search"
                />
              </div>
            </div>
            <p className="mt-6 text-xs text-slate-500 leading-relaxed">{preset.footnote}</p>
          </div>

          <div className="lg:col-span-8">
            <div className="flex items-baseline justify-between mb-4">
              <p className="text-sm text-slate-600" data-testid="amm-task-count">
                <span className="font-bold text-[#1e2d3b]">{count}</span> task{count === 1 ? '' : 's'} for <span className="font-semibold text-[#1e2d3b]">{selected?.label}</span>
              </p>
            </div>
            <div className="space-y-4">
              {groups.map((g) => <TaskGroup key={g.category} category={g.category} tasks={g.tasks} />)}
              {!count && <p className="text-sm text-slate-400 italic py-8 text-center" data-testid="amm-task-empty">No tasks match that filter.</p>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
